// apps/merchant-v3/src/components/LanguageSelector.tsx
//
// Phase 6 Wave 1 Task 02 — TopBar language selector EN/VI/FR.
// Phase 6 Wave 2 Task 06 — Wires the real i18next switcher: on select
// we call `i18n.changeLanguage()` (which the LanguageDetector also
// persists to cookie + localStorage), then mirror the value to
// `user.language` Zustand so the next /merchants/me PATCH ships the
// preference server-side. Server PATCH itself lands in Wave 11
// (Settings/Profile) — until then the cookie keeps it sticky.
//
// 2026-05-29 fix(debug): F5 reload was clobbering the local pick because
// useMe() refetches GET /auth/me on mount and overwrites Zustand with
// server-truth. Now we PATCH /auth/me with the new language and
// invalidate the useMe cache so the round-trip is symmetric.
//
// vg-binding: PLAN:task-02
// vg-binding: PLAN:task-06
// vg-binding: P6.D-05
// vg-binding: P6.D-131
// vg-binding: P6.D-221

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@printway/ui';
import { Languages, Check } from 'lucide-react';

import { SUPPORTED_LANGUAGES, changeLanguage, useTranslation } from '@/lib/i18n';
import { useUser, useUserStore } from '@/store/user';
import type { SupportedLanguage } from '@/types/user';
import { cn } from '@/lib/cn';
import { updateProfile } from '@/services/settingsClient';
import { useInvalidateMe } from '@/lib/auth/useMe';

export function LanguageSelector(): JSX.Element {
  const user = useUser();
  const setUser = useUserStore((s) => s.setUser);
  const invalidateMe = useInvalidateMe();
  const { t } = useTranslation('common');
  const current: SupportedLanguage = user?.language ?? 'VI';

  const handleSelect = (code: SupportedLanguage): void => {
    void changeLanguage(code);
    const prev = user;
    if (user) {
      setUser({ ...user, language: code });
    }
    // Persist server-side so F5 → useMe() refetch returns the same value.
    // Lowercase per UpdateProfilePayload contract ('vi' | 'en' | 'fr').
    void (async () => {
      try {
        await updateProfile({
          language: code.toLowerCase() as 'vi' | 'en' | 'fr',
        });
        await invalidateMe();
      } catch {
        // Roll back the optimistic local update if the BE rejected it.
        if (prev) setUser(prev);
      }
    })();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('topbar.selectLanguage')}
          className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm font-medium text-pw-text-muted hover:bg-pw-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <Languages className="h-4 w-4" aria-hidden />
          <span>{current}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="z-50 min-w-[10rem] rounded-md border border-pw-border bg-pw-card p-1 shadow-md"
      >
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onSelect={() => handleSelect(lang.code)}
            className={cn(
              'flex cursor-pointer items-center justify-between rounded px-2 py-1.5 text-sm text-pw-text-muted outline-none',
              'focus:bg-pw-surface data-[highlighted]:bg-pw-surface',
            )}
          >
            <span>{lang.label}</span>
            {current === lang.code ? (
              <Check className="h-3.5 w-3.5 text-brand-500" aria-hidden />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSelector;
